import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IColumnSetting, ICsvPreview, ICsvPreviewCell, IFileCenterSampleData } from '../../../settings/settingsTypes'
import { useCsvPreview, useCsvPreviewSniff } from '../../../claims/claimsHooks'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { FaTimesCircle } from 'react-icons/fa'
import { theme } from '../../../../components/Theme'

export interface ICsvPreviewGridProps {
  fileCenterSampleData: IFileCenterSampleData
  columnSettings: IColumnSetting[]
  justSniff: boolean
}

const Changed = (csvPreviewCell: ICsvPreviewCell): boolean => {
  return csvPreviewCell.original != null && csvPreviewCell.original != csvPreviewCell.transformed
}
const New = (csvPreviewCell: ICsvPreviewCell): boolean => csvPreviewCell.original == null

const ColDefOfHeaderCell = (csvPreviewCellHeader: ICsvPreviewCell, columnNumber: number): ColDef<ICsvPreviewCell[]> => {
  const columnIsNew = New(csvPreviewCellHeader)
  return {
    colId: columnNumber.toString(),
    headerName: `${csvPreviewCellHeader.transformed}`,
    headerTooltip: `#${columnNumber}`,
    lockPinned: true,
    cellClass: columnIsNew ? 'new-column-cell' : undefined,
    headerClass: columnIsNew ? 'new-column-header' : undefined,
    cellRenderer: (cellRenderParams: ICellRendererParams<ICsvPreviewCell[]>) => {
      const csvPreviewCellBodies = cellRenderParams.data
      if (csvPreviewCellBodies == null) return <span>Error (1)</span>
      const csvPreviewCellBody = csvPreviewCellBodies[columnNumber]
      if (csvPreviewCellBody == null) return <span>Error (2)</span>
      if (Changed(csvPreviewCellBody))
        return (
          <>
            <span className="replaced">{csvPreviewCellBody.original}</span>{' '}
            <span className="replacement">{csvPreviewCellBody.transformed}</span>
          </>
        )
      if (!columnIsNew && New(csvPreviewCellBody)) return <span className="new">{csvPreviewCellBody.transformed}</span>
      return <span className="unchanged">{csvPreviewCellBody.transformed}</span>
    },
  }
}

const ComputeColDefs = (csvPreview?: ICsvPreview): ColDef<ICsvPreviewCell[]>[] => {
  if (!csvPreview?.header) return []
  const colDefsInNaturalOrder = csvPreview.header.map(ColDefOfHeaderCell)
  return colDefsInNaturalOrder.sort((colA, colB) => {
    if (!colA.colId || !colB.colId) {
      if (!colA.colId) return 1
      return -1
    }
    if (colA.headerClass == 'new-column-header' && colB.headerClass == 'new-column-header')
      return (colA.headerName ?? '').localeCompare(colB.headerName ?? '')
    if (colA.headerClass == colB.headerClass) return parseInt(colA.colId) - parseInt(colB.colId)
    if (colA.headerClass == 'new-column-header') return -1
    return 1
  })
}

const CsvPreviewGrid = ({ fileCenterSampleData, columnSettings, justSniff }: ICsvPreviewGridProps) => {
  const {
    data: csvPreview,
    error,
    isLoading,
    mutate,
  } = justSniff
      ? useCsvPreviewSniff({
        csv: fileCenterSampleData.csvText,
      })
      : useCsvPreview({
        columnSettings: columnSettings,
        csv: fileCenterSampleData.csvText,
      })
  if (!error) {
    mutate()
  }

  const [columnDefs, setColumnDefs] = useState<ColDef<ICsvPreviewCell[]>[]>()
  useEffect(() => {
    setColumnDefs(ComputeColDefs(csvPreview))
  }, [csvPreview])

  // handlers
  return (
    <Div>
      {isLoading ? (
        <div className="label">Loading...</div>
      ) : (
        <div className="problems-and-preview-grid">
          {!csvPreview ? (
            <div>No preview available</div>
          ) : (
            <>
              {csvPreview.requiredButMissingColumns != null && csvPreview.requiredButMissingColumns.length > 0 ? (
                <div className="problems">
                  <h2>Potential problems</h2>
                  <p>The following columns are required for claims upload but appear to be missing:</p>
                  <ul className="column-problem-list">
                    {csvPreview.requiredButMissingColumns.map((column) => (
                      <li key={column}>
                        <FaTimesCircle color={theme.colors.warning.neutral} />
                        {column}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="problems">
                  <h2>Preview meets basic requirements for upload.</h2>
                </div>
              )}
              <div className="preview-grid">
                <AgGridReact<ICsvPreviewCell[]>
                  className="ag-theme-alpine ag-theme-outbound"
                  rowData={csvPreview.cellGrid}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    sortable: false,
                    resizable: true,
                    width: 150,
                  }}
                  enableCellTextSelection={true}
                  suppressDragLeaveHidesColumns={true}
                />
              </div>
            </>
          )}
        </div>
      )}
    </Div>
  )
}

const Div = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 0.5rem;
  border: none;

  .problems-and-preview-grid {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .problems {
    height: fit-content;
  }

  .preview-grid {
    flex-grow: 1;
    height: 100%;
  }

  ul.column-problem-list {
  }

  ul.column-problem-list > li {
    display: inline-block;
    margin-left: 1rem;
  }

  .new-column-cell {
    background-color: ${({ theme }) => theme.colors.success.lighter};
  }

  .new-column-header {
    background-color: ${({ theme }) => theme.colors.success.light};
  }

  .new {
    background-color: ${({ theme }) => theme.colors.success.light};
  }

  .unchanged {
  }

  .replaced {
    text-decoration: line-through;
  }

  .replacement {
    background-color: ${({ theme }) => theme.colors.success.light};
  }

  > .label {
    position: absolute;
    top: 0.5rem;
    right: 2.25rem;
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    ${({ theme }) => theme.typography.s.normal};
    font-style: italic;
    color: ${({ theme }) => theme.colors.primary.dark};
  }
`

export { CsvPreviewGrid }
